import { useState, useEffect } from 'react';
import { useAuth } from '../lib/AuthContext';
import { accountApi } from '../api/client';

export function useAccountData() {
  const { user, isLoading: authLoading } = useAuth();
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [personalForm, setPersonalForm] = useState({ firstName: '', lastName: '', phone: '' });
  const [addressForm, setAddressForm] = useState({ street: '', number: '', bus: '', postalCode: '', city: '', country: 'BE' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (authLoading || !user) return;
    accountApi.get().then(r => {
      setAccount(r.data);
      const p = r.data.profile || r.data.b2bPerson || null;
      if (p) {
        setPersonalForm({ firstName: p.firstName || '', lastName: p.lastName || '', phone: p.phone || '' });
      } else {
        // Legacy orphan: no customer_profile yet. Conservative split of
        // users.name so the inputs aren't blank when the user opens the
        // "Bewerken" form. Persisted on first save via PUT /account/profile.
        const parts = (r.data.user?.name || '').trim().split(/\s+/);
        const firstName = parts[0] || '';
        const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';
        setPersonalForm({ firstName, lastName, phone: '' });
      }
      if (r.data.profile) {
        setAddressForm({
          street: r.data.profile.street || '', number: r.data.profile.number || '',
          bus: r.data.profile.bus || '', postalCode: r.data.profile.postalCode || '',
          city: r.data.profile.city || '', country: r.data.profile.country || 'BE',
        });
      }
    }).catch(err => {
      if (err.response?.status === 401) setAccount(null);
      // Non-auth errors are swallowed — dashboard falls back to empty state.
    }).finally(() => setLoading(false));
  }, [user, authLoading]);

  const savePersonal = async () => {
    setSaving(true);
    try {
      await accountApi.updateProfile(personalForm);
      setAccount(prev => {
        const updated = { ...prev };
        if (updated.b2bPerson) updated.b2bPerson = { ...updated.b2bPerson, ...personalForm };
        if (updated.profile) updated.profile = { ...updated.profile, ...personalForm };
        return updated;
      });
      return true;
    } catch (err) {
      alert(err.response?.data?.detail || 'Fout bij opslaan');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const saveAddress = async () => {
    setSaving(true);
    try {
      await accountApi.updateProfile(addressForm);
      setAccount(prev => ({ ...prev, profile: { ...prev.profile, ...addressForm } }));
      return true;
    } catch (err) {
      alert(err.response?.data?.detail || 'Fout bij opslaan');
      return false;
    } finally {
      setSaving(false);
    }
  };

  return {
    user, authLoading, account, loading, saving,
    personalForm, setPersonalForm, addressForm, setAddressForm,
    savePersonal, saveAddress,
    isB2B: Boolean(account?.b2bPerson),
    profile: account?.profile, b2bPerson: account?.b2bPerson, b2bOrg: account?.b2bOrg,
    orders: account?.orders || [],
  };
}
